import React, { useCallback, useState, forwardRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  Box, 
  Button, 
  Typography, 
  CircularProgress, 
  Paper,
  Link
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from 'axios';
import logger from './../utils/logger.js';
import api from '../api.js';
import FilePreview from './FilePreview';

const FileUpload = forwardRef(({ onFileUpload, disabled = false, loading = false }, ref) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [error, setError] = useState(null);
  
  
  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setSelectedFile(file);
    }
  }, []);
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv']
    },
    multiple: false,
    disabled: disabled || previewing || loading,
    noClick: false,
    noKeyboard: false,
    preventDropOnDocument: true
  });
  
  const handlePreview = async () => {
    if (!selectedFile) return;

    setPreviewing(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const uploadApi = axios.create({
        baseURL: api.defaults.baseURL,
        timeout: 1200000,
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });

      const response = await uploadApi.post('/api/files/preview', formData);

      if (response.data && response.data.success) {
        setPreviewData(response.data);
        setPreviewOpen(true);
      } else {
        setError(response.data?.message || 'Failed to preview file');
      }
    } catch (error) {
      logger.error('Error previewing file:', error);
      if (error.response) {
        setError(`Preview failed: ${error.response.data?.message || error.response.statusText}`);
      } else if (error.request) {
        setError('Preview failed: No response received from server');
      } else {
        setError(`Preview failed: ${error.message}`);
      }
    } finally {
      setPreviewing(false);
    }
  };

  const handleImportPreview = async (importOptions) => {
    setPreviewing(true);
    setError(null);

    try {
      const response = await api.post('/api/files/import-preview', importOptions);

      if (response.data && response.data.success) {
        // Close preview and reset
        setPreviewOpen(false);
        setPreviewData(null);
        setSelectedFile(null);
        
        // Notify parent component
        if (onFileUpload) {
          await onFileUpload(null, null, response.data);
        }
      } else {
        // Throw error so FilePreview can catch it
        const error = new Error(response.data?.message || 'Failed to import data');
        error.response = { data: response.data };
        throw error;
      }
    } catch (error) {
      logger.error('Error importing preview:', error);
      // Re-throw error so FilePreview can handle it
      throw error;
    } finally {
      setPreviewing(false);
    }
  };
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <Paper
        {...getRootProps()}
        role="button"
        tabIndex={0}
        sx={{
          p: 3,
          border: isDragActive ? '2px dashed #1976d2' : '2px dashed #ccc',
          borderRadius: 2,
          bgcolor: isDragActive ? 'rgba(25, 118, 210, 0.04)' : 'background.paper',
          cursor: disabled || previewing || loading ? 'not-allowed' : 'pointer',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'none',
          opacity: disabled || previewing || loading ? 0.7 : 1,
          '&:hover': {
            borderColor: disabled || previewing || loading ? '#ccc' : '#1976d2',
            bgcolor: disabled || previewing || loading ? 'inherit' : 'rgba(25, 118, 210, 0.04)'
          },
          '&:focus': {
            outline: 'none',
            borderColor: '#1976d2',
            boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)'
          }
        }}
        onClick={(e) => {
          if (!disabled && !previewing && !loading) {
            if (getRootProps().onClick) {
              getRootProps().onClick(e);
            }
          }
        }}
      >
        <input {...getInputProps()} ref={ref} data-testid="file-input" />
        <CloudUploadIcon sx={{ fontSize: 48, color: isDragActive ? 'primary.main' : 'text.secondary', mb: 2 }} />
        
        <Typography variant="h6" component="div" gutterBottom align="center">
          {isDragActive ? 'Drop the Excel file here' : 'Drag & drop an Excel file here'}
        </Typography>
        
        <Typography variant="body2" color="textSecondary" align="center">
          or click to select a file (.xlsx, .xls, .csv)
        </Typography>
        
        {/* Browser fallback for click issues */}
        {(disabled || previewing || loading) ? null : (
          <Typography variant="body2" color="primary" align="center" sx={{ mt: 1 }}>
            <Link 
              component="button" 
              variant="body2" 
              onClick={(e) => {
                e.stopPropagation();
                // Directly trigger the hidden input
                const fileInput = document.querySelector('input[type="file"]');
                if (fileInput) {
                  fileInput.click();
                }
              }}
              sx={{ textDecoration: 'underline', cursor: 'pointer' }}
            >
              Click here if the upload area is not clickable
            </Link>
          </Typography>
        )}
      </Paper>
      
      {selectedFile && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <CheckCircleIcon color="success" />
          <Typography variant="body2">
            {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
          </Typography>
        </Box>
      )}

      {error && (
        <Paper sx={{ p: 2, width: '100%', mt: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
          <Typography variant="body2">{error}</Typography>
        </Paper>
      )}
      
      <Button 
        variant="contained" 
        color="primary" 
        size="large" 
        onClick={handlePreview}
        disabled={!selectedFile || disabled || previewing || loading}
        startIcon={(previewing || loading) ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
        sx={{ mt: 2, minWidth: 200 }}
      >
        {(previewing || loading) ? 'Laden...' : 'Preview & Import'}
      </Button>

      {previewData && (
        <FilePreview
          open={previewOpen}
          onClose={() => {
            setPreviewOpen(false);
            setPreviewData(null);
          }}
          previewData={previewData}
          onImport={handleImportPreview}
          loading={previewing}
        />
      )}
    </Box>
  );
});

export default FileUpload; 